import { ApPayeeProvider } from '@/components/ApPayee';
import { useLoginInfo } from '@/components/LoginInfo/provider';
import { Wrapper } from '@/components/OpenFeature/Wrapper';
import { FinishedProviderSetting } from '@/components/OpenFeature/Wrapper/finishedProviderSetting';
import { useFFContext } from '@/components/OpenFeature/hooks/useFFContext';
import { PopupContainerProvider } from '@/components/ReportForm/Container/PopupContainer';
import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { AgentApplicantOfficeMemberProvider } from '@/context/AgentApplicantOfficeMember';
import { TpmInvoiceLoadingProvider } from '@/context/TpmInvoiceLoading';
import { useGetApPayeeDetailOrUndefinedSuspense } from '@/context/services/ap_payee/ApPayee.service';
import { useGetTransactionProperties } from '@/context/services/invoice_report/TransactionProperties.service';
import { useGetMfFile } from '@/context/services/mf_file/MfFile.service';
import {
  useGetInvoiceReport,
  useGetInvoiceTransaction,
} from '@/context/services/reportsType/invoiceReports/Edit.service';
import { findReportFormInputTypeValue } from '@/context/services/reportsType/invoiceReports/useConvertApiDataAndPayeeForm';
import { filteredIsHiddenReportFormInputs } from '@/context/services/reportsType/invoiceReports/useConvertApiDataAndReportForm';
import {
  useConvertTpmResponseToBusinessPersonData,
  useGetTpmInvoicesSuspense,
} from '@/context/services/tpm/hooks';
import { InvoiceReportEdit } from '@/features/InvoiceReport/Edit/components/InvoiceReportEdit';
import { Route } from '@/features/InvoiceReport/Edit/type';
import { checkIsServerError } from '@/features/errors/utils';
import { OpenFeatureProvider } from '@moneyforward/user-flex-feature-hooks';
import { FC, memo, useMemo } from 'react';
import { ApiProvider, ComponentAPI } from '../components/Context';

export type Props = {
  invoiceReportsId: string;
  route: Route;
};

const InnerEditTemplate: FC<Props> = memo(({ invoiceReportsId, route }) => {
  const formData = useGetInvoiceReport(invoiceReportsId);
  const apiData = useMemo(
    () => ({
      ...formData,
      report_form_inputs: filteredIsHiddenReportFormInputs(
        formData.report_form_inputs || []
      ),
    }),
    [formData]
  );
  const transactionData = useGetInvoiceTransaction(invoiceReportsId);
  const invoiceFile = useGetMfFile(
    apiData.invoice_file?.id,
    Boolean(apiData.invoice_file?.id)
  );
  const registrationNumber = findReportFormInputTypeValue(
    apiData.report_form_inputs,
    'ApReportFormInputInvoiceRegistrationNumberField'
  );
  const payeeId = findReportFormInputTypeValue(
    apiData.report_form_inputs,
    'ApReportFormInputInvoicePayeeSelect'
  );

  const resp = useGetTpmInvoicesSuspense(registrationNumber);
  const businessPersonData = useConvertTpmResponseToBusinessPersonData(resp);
  const payee = useGetApPayeeDetailOrUndefinedSuspense(payeeId || '');
  const { currentOfficeMember } = useLoginInfo();
  const isAgentApplicantOfficeMember = useMemo(() => {
    return (
      ['normal'].includes(route) &&
      apiData.proxy_office_member &&
      apiData.office_member?.id !== currentOfficeMember?.id
    );
  }, [
    route,
    apiData.proxy_office_member,
    apiData.office_member?.id,
    currentOfficeMember?.id,
  ]);

  const context = useFFContext();

  const { data: transactionProperties } =
    useGetTransactionProperties(invoiceReportsId);

  return (
    <PopupContainerProvider>
      <Wrapper isProto={false}>
        <OpenFeatureProvider
          pollInterval={-1}
          context={context}
          domain='ap-validate-maximum-invoice-transaction-count'
        >
          <OpenFeatureProvider
            pollInterval={-1}
            context={context}
            domain='ap-data-extraction-mismatch-alert'
          >
            <OpenFeatureProvider
              pollInterval={-1}
              context={context}
              domain='ap-data-extraction-mismatch-alert-phase-2'
            >
              <OpenFeatureProvider
                pollInterval={-1}
                context={context}
                domain='ap-keep-approval-search-conditions'
              >
                <ApiProvider
                  formValue={apiData}
                  detailValue={transactionData}
                  invoiceFile={apiData.invoice_file ? invoiceFile : undefined}
                  businessPersonData={businessPersonData}
                  route={route}
                  transactionProperties={transactionProperties}
                >
                  <AgentApplicantOfficeMemberProvider
                    officeMember={
                      isAgentApplicantOfficeMember
                        ? apiData.office_member?.id
                        : undefined
                    }
                    report_number_for_expense
                    project
                    dept
                    ap_payee
                    ocr_invoice_inference
                  >
                    <AgentApplicantOfficeMemberProvider
                      officeMember={
                        ['normal'].includes(route)
                          ? apiData.office_member?.id
                          : undefined
                      }
                      received_invoices
                      business_document
                      mf_file
                    >
                      <ComponentAPI>
                        <FinishedProviderSetting>
                          <ApPayeeProvider value={payee}>
                            <TpmInvoiceLoadingProvider>
                              <InvoiceReportEdit
                                invoiceReportsId={invoiceReportsId}
                              />
                            </TpmInvoiceLoadingProvider>
                          </ApPayeeProvider>
                        </FinishedProviderSetting>
                      </ComponentAPI>
                    </AgentApplicantOfficeMemberProvider>
                  </AgentApplicantOfficeMemberProvider>
                </ApiProvider>
              </OpenFeatureProvider>
            </OpenFeatureProvider>
          </OpenFeatureProvider>
        </OpenFeatureProvider>
      </Wrapper>
    </PopupContainerProvider>
  );
});
InnerEditTemplate.displayName = 'InnerEditTemplate';

export const EditTemplate: FC<Props> = memo(({ invoiceReportsId, route }) => {
  const loadingMemo = useMemo(
    () => ({ isFullFlame: true, isOverlay: true } as const),
    []
  );
  return (
    <SuspenseErrorBoundary
      shouldPropagate={checkIsServerError}
      loading={loadingMemo}
    >
      <InnerEditTemplate invoiceReportsId={invoiceReportsId} route={route} />
    </SuspenseErrorBoundary>
  );
});
EditTemplate.displayName = 'Edit';
